import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
    forwardRef
} from '@angular/core';
import { AutocompleteOptionComponent } from './autocomplete-option/autocomplete-option.component';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import { autocompleteStylesSAG, autocompleteStylesSHS } from './autocomplete.styles';
import { componentStylesByTheme } from '../utils/theme.utils';
import { Subscription } from 'rxjs';

@Component({
    selector: 'sag-ui-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: [],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => AutocompleteComponent),
            multi: true
        }
    ]
})
export class AutocompleteComponent
    implements AfterContentInit, ControlValueAccessor, Validator, OnDestroy, AfterViewInit
{
    @Input() id = this.randomId();
    /**
     * The placeholder of the Autocomplete.
     */
    @Input()
    placeholder = '';

    /*
     * Whether the Autocomplete is required.
     */
    @Input()
    required = false;

    /**
     * Whether the Autocomplete is disabled.
     */
    @Input()
    disabled = false;

    /**
     * Whether the Autocomplete is read-only.
     */
    @Input()
    readOnly = false;

    /**
     * The label of the Autocomplete.
     */
    @Input()
    label = '';

    /**
     * The value of the Autocomplete.
     */
    @Input()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any = '';

    /**
     * Whether the Select is in an error state.
     */
    @Input() invalid = false;

    /**
     * The error message to display when the Select is in an error state.
     */
    @Input() error: string | string[] = '';

    /**
     * A function to compare the selected value with the option value.
     */
    @Input() compareWith: (o1: any, o2: any) => boolean = (o1, o2) => {
        if (typeof o1 === 'object' && typeof o2 === 'object') {
            return JSON.stringify(o1) === JSON.stringify(o2);
        }

        return o1 === o2;
    };

    @Input() hint?: string;
    @Input() floatLabel = false;

    // Accessibility inputs
    @Input() ariaLabel: string | null = null;
    @Input() ariaLabelledby: string | null = null;
    @Input() ariaDescribedby: string | null = null;

    // Outputs for events
    @Output() change: EventEmitter<Event> = new EventEmitter<Event>();
    @Output() click = new EventEmitter<Event>();
    @Output() focus = new EventEmitter<Event>();
    @Output() blur = new EventEmitter<Event>();
    @Output() keydown = new EventEmitter<KeyboardEvent>();
    @Output() keyup = new EventEmitter<KeyboardEvent>();
    @Output() mouseenter = new EventEmitter<MouseEvent>();
    @Output() mouseleave = new EventEmitter<MouseEvent>();

    @ContentChildren(AutocompleteOptionComponent)
    optionComponents!: QueryList<AutocompleteOptionComponent>;

    @ViewChild('input') input!: ElementRef;

    @HostListener('document:mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen = false;
        }
    }

    @HostListener('focusout', ['$event'])
    onFocusOut(event: FocusEvent): void {
        const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
        if (!focusedInside) {
            this.isOpen = false;
        }
    }

    @HostListener('click')
    @HostListener('mouseenter')
    checkDropdownPosition() {
        const selectBox = this.el.nativeElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        this.isDropdownUpwards = selectBox.bottom + 200 > viewportHeight;
    }

    touched = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected selectedItems: [string, any][] = [];
    protected isOpen = false;
    protected focused = false;
    private filterValue = '';
    protected isDropdownUpwards = false;

    private subscriptions: Subscription[] = [];

    constructor(@Inject(THEME_TOKEN) private readonly theme: Theme, private el: ElementRef) {}

    public ngAfterViewInit(): void {
        // Prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.initSelectedItems();
        }, 0);
    }

    public ngOnDestroy(): void {
        this.resetSubscriptions();
    }

    private resetSubscriptions(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    private setupSubscriptions(): void {
        this.optionComponents.forEach(option => {
            this.subscriptions.push(
                option.autocomplete.subscribe(data => {
                    this.handleOptionSelect(data);
                })
            );
        });
    }

    private subscribeToOptionComponentChanges(): void {
        this.optionComponents.changes.subscribe(() => {
            this.resetSubscriptions();
            this.setupSubscriptions();
            this.initSelectedItems();
        });
    }

    private handleOptionSelect(data: [string, any]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const [label, value] = data as [string, any];

        this.input.nativeElement.focus();
        this.markAsTouched();

        if (this.selectedItems[0] && this.compareWith(this.selectedItems[0][1], value)) {
            this.selectedItems = [];
        } else {
            this.selectedItems = [[label, value]];
        }

        this.writeValue(this.selectedItems[0] ? this.selectedItems[0][1] : undefined);
        this.isOpen = false;
        setTimeout(() => {
            this.input.nativeElement.blur();
        }, 50);

        this.updateSelectedOptions();
    }

    public ngAfterContentInit(): void {
        this.setupSubscriptions();
        this.subscribeToOptionComponentChanges();
    }

    protected get popoverClasses() {
        const popoverStyles = componentStylesByTheme(this.theme, autocompleteStylesSAG, autocompleteStylesSHS);

        return popoverStyles();
    }

    private initSelectedItems(): void {
        this.updateSelectedOptions();

        if (this.optionComponents) {
            this.selectedItems = this.optionComponents
                .filter(option => option.selected)
                .map(option => [option.text.nativeElement.innerText, option.value]);
        }
    }

    private updateSelectedOptions() {
        this.optionComponents?.forEach(option => {
            option.selected = this.compareWith(this.value, option.value);
        });
    }

    protected get displayValue(): string {
        return this.selectedItems.map(item => item[0]).join(', ');
    }

    protected onFocus() {
        if (this.disabled || this.readOnly) return;

        this.filterValue = this.selectedItems[0] ? this.selectedItems[0][0] : '';
        this.filterOptions();
        this.focused = true;
        this.isOpen = true;
    }

    protected onBlur() {
        this.focused = false;
        setTimeout(() => {
            this.markAsTouched();
        }, 100);
    }

    protected togglePopover(event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        if (this.isOpen) {
            this.isOpen = false;
            this.input.nativeElement.blur();
        } else {
            this.isOpen = true;
            this.input.nativeElement.focus();
        }
    }

    private filterOptions() {
        this.optionComponents.forEach(option => {
            option.hidden = !option.text.nativeElement.innerText.toLowerCase().includes(this.filterValue.toLowerCase());
        });
    }

    protected handleInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.filterValue = target.value;

        this.filterOptions();
    }

    protected handleChange() {
        this.selectedItems = [];
        this.updateSelectedOptions();
        this.writeValue('');
    }

    // eslint-disable-next-line
    onChange = (_value: any) => {};
    // eslint-disable-next-line
    onTouch = () => {};

    // eslint-disable-next-line
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    // eslint-disable-next-line
    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    writeValue(val: any) {
        this.value = val;
        this.onChange(this.value);
        this.change.emit(this.value);

        try {
            this.initSelectedItems();
        } catch (e) {
            console.error(e);
        }
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }

    // eslint-disable-next-line
    validate(control: AbstractControl<any, any>): ValidationErrors | null {
        return this.required && !control.value.length ? { required: true } : null;
    }

    blockChange(event: Event) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (event.target && (event.target as any)['value']) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (event.target as any)['value'] = this.displayValue;
        }
    }

    isArray(value: string | string[]): value is string[] {
        return Array.isArray(value);
    }

    private randomId(): string {
        return Math.random().toString(36).substring(2);
    }
}
